import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Card, PageHeader, Button } from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  getChatInboxThunk,
  getChatMessagesThunk,
  sendChatMessageThunk,
  setActiveOtherPartyId,
} from "../../../redux";
import { showSuccess, showError } from "../../../services";
import { formatChatMessageTime } from "../../../utils";
import refreshIcon from "../../../assets/icons/refresh.svg";
import "./TeacherInbox.css";

const TeacherInbox: React.FC = () => {
  const dispatch = useAppDispatch();
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const initialScrolledPartiesRef = useRef<Record<string, boolean>>({});
  const shouldScrollToBottomRef = useRef<boolean>(false);
  const prevScrollHeightRef = useRef<number>(0);
  const prevScrollTopRef = useRef<number>(0);
  const isPrependingRef = useRef<boolean>(false);

  const {
    conversations,
    activeOtherPartyId,
    messagesByParty,
    hasMoreByParty,
    isFetchingOlderByParty,
    isLoading,
  } = useAppSelector((state) => state.chat);

  const [messageInput, setMessageInput] = useState<string>("");

  useEffect(() => {
    dispatch(getChatInboxThunk({ force: true }));
  }, [dispatch]);

  const activeConv = conversations.find(
    (c) => c.otherPartyId === activeOtherPartyId,
  );
  const activeMessages = activeOtherPartyId
    ? messagesByParty[activeOtherPartyId] || []
    : [];
  const isFetchingOlder = activeOtherPartyId
    ? !!isFetchingOlderByParty[activeOtherPartyId]
    : false;
  const hasMore = activeOtherPartyId
    ? hasMoreByParty[activeOtherPartyId] !== false
    : true;

  const handleRefresh = () => {
    dispatch(getChatInboxThunk({ force: true }));
    if (activeOtherPartyId) {
      initialScrolledPartiesRef.current[activeOtherPartyId] = false;
      dispatch(
        getChatMessagesThunk({ recipientId: activeOtherPartyId, force: true }),
      );
    }
  };

  useEffect(() => {
    if (activeOtherPartyId) {
      dispatch(
        getChatMessagesThunk({ recipientId: activeOtherPartyId, force: true }),
      );
    }
  }, [dispatch, activeOtherPartyId]);

  useLayoutEffect(() => {
    const container = chatContainerRef.current;
    if (!container || !activeOtherPartyId) return;

    if (isPrependingRef.current) {
      container.scrollTop =
        prevScrollTopRef.current +
        (container.scrollHeight - prevScrollHeightRef.current);
      isPrependingRef.current = false;
    } else if (
      shouldScrollToBottomRef.current ||
      (!initialScrolledPartiesRef.current[activeOtherPartyId] &&
        activeMessages.length > 0)
    ) {
      container.scrollTop = container.scrollHeight;
      initialScrolledPartiesRef.current[activeOtherPartyId] = true;
      shouldScrollToBottomRef.current = false;
    }
  }, [activeMessages, activeOtherPartyId]);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container || !activeOtherPartyId) return;

    if (
      container.scrollTop <= 20 &&
      hasMore &&
      !isFetchingOlder &&
      !isLoading
    ) {
      prevScrollHeightRef.current = container.scrollHeight;
      prevScrollTopRef.current = container.scrollTop;
      isPrependingRef.current = true;

      dispatch(
        getChatMessagesThunk({
          recipientId: activeOtherPartyId,
          offset: activeMessages.length,
          limit: 10,
          isOlder: true,
        }),
      );
    }
  };

  const handleSelectConversation = (otherPartyId: string) => {
    dispatch(setActiveOtherPartyId(otherPartyId));
    setMessageInput("");
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = messageInput.trim();

    if (!trimmed) {
      showError("Please enter a message to send.");
      return;
    }

    if (!activeOtherPartyId) return;

    shouldScrollToBottomRef.current = true;
    dispatch(
      sendChatMessageThunk({
        recipientId: activeOtherPartyId,
        message: trimmed,
      }),
    ).then((res) => {
      if (sendChatMessageThunk.fulfilled.match(res)) {
        showSuccess("Message sent successfully!");
        setMessageInput("");
      }
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div className="chat-container-card">
        {/* Sidebar Conversation List */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <h3>Conversations</h3>
            <button
              type="button"
              className="chat-refresh-btn"
              onClick={handleRefresh}
              title="Refresh conversations"
              aria-label="Refresh conversations"
            >
              <img
                src={refreshIcon}
                alt="Refresh"
                className={`chat-refresh-icon ${isLoading ? "spinning" : ""}`}
              />
            </button>
          </div>

          <div className="chat-threads-list">
            {conversations.length === 0 ? (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "#8c8594",
                }}
              >
                No active conversations
              </div>
            ) : (
              conversations.map((item) => {
                const isActive = item.otherPartyId === activeOtherPartyId;
                const name =
                  item.otherPartyName ||
                  `Student (${item.otherPartyId.slice(-4)})`;
                return (
                  <div
                    key={item.otherPartyId}
                    className={`chat-thread-item ${isActive ? "active" : ""}`}
                    onClick={() => handleSelectConversation(item.otherPartyId)}
                  >
                    <div className="chat-avatar">
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div className="chat-thread-info">
                      <div className="chat-thread-top">
                        <span className="chat-participant-name">{name}</span>
                        <span className="chat-last-time">
                          {formatChatMessageTime(item.lastMessageAt, "list")}
                        </span>
                      </div>
                      <div className="chat-thread-bottom">
                        <span className="chat-last-msg">
                          {item.lastMessage}
                        </span>
                        {item.unreadCount > 0 && (
                          <span className="chat-unread-badge">
                            {item.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Main Chat Conversation View */}
        {activeConv ? (
          <div className="chat-main">
            <div className="chat-main-header">
              <div className="chat-avatar">
                {(activeConv.otherPartyName || "S").charAt(0).toUpperCase()}
              </div>
              <div className="chat-header-info">
                <div className="chat-header-title">
                  {activeConv.otherPartyName ||
                    `Student (${activeConv.otherPartyId})`}
                </div>
                <span className="chat-header-role">Student</span>
              </div>
            </div>

            <div
              className="chat-messages-body"
              ref={chatContainerRef}
              onScroll={handleScroll}
            >
              {isFetchingOlder && (
                <div className="chat-loading-older">
                  Loading older messages...
                </div>
              )}
              {activeMessages.length === 0 ? (
                <div className="empty-chat-placeholder">
                  No messages yet. Send a message to start!
                </div>
              ) : (
                activeMessages.map((msg) => {
                  const isSentByTeacher = msg.Sender_Type === "Teacher";
                  return (
                    <div
                      key={msg.id}
                      className={`chat-msg-bubble ${isSentByTeacher ? "sent" : "received"}`}
                    >
                      <div>{msg.Message}</div>
                      <div className="chat-msg-time">
                        {formatChatMessageTime(msg.Created_Time, "bubble")}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Single WhatsApp-style Chat Input Bar */}
            <form onSubmit={handleSendMessage} className="chat-input-area">
              <input
                type="text"
                placeholder="Type a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="chat-input-field"
              />
              <Button type="submit" variant="primary" size="medium">
                Send
              </Button>
            </form>
          </div>
        ) : (
          <div className="empty-chat-placeholder">
            Select a conversation to start chatting
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherInbox;
